import { createFeatureSelector, createSelector, MemoizedSelector, Selector } from '@ngrx/store';

import { AppLockState as LockState } from '@era/settings-sdk';
import * as fromAppLock from './category-app-lock.reducer';
import { AppLock, AppLockState } from './category-app-lock.state';

import { ModelNameFlags } from '@era/locations-sdk';
import { createSelectApplicationsLinkedToCategory } from '../category-applications/category-applications.selector';
import { ApplicationConfiguration } from '../category-applications/category-applications.state';
import { Category } from '../router/category';

// ============================================================================
// STATE SELECTORS
// ============================================================================

export const selectAppLockState = createFeatureSelector<AppLockState>('app-lock');

export const selectAppLockIds = createSelector(selectAppLockState, fromAppLock.selectAppLockIds);

export const selectAppLockEntities = createSelector(selectAppLockState, fromAppLock.selectAppLockEntities);

export const selectAllAppLocks = createSelector(selectAppLockState, fromAppLock.selectAllAppLocks);

// ============================================================================
// FACTORY FUNCTIONS
// ============================================================================

// Generic selector factory functions that work with any CategoryType
// These eliminate the need to replicate selectors for every category type

export const createSelectAppLockLinkedToCategory = (
  appLockState: LockState,
  modelName: ModelNameFlags,
  category: Category
) => {
  return createSelector(selectAllAppLocks, (appLocks: AppLock[]) => {
    if (!category) {
      return undefined;
    }
    return appLocks.find((appLock: AppLock) => {
      // Check modelName match
      if (appLock.modelName !== modelName) {
        return false;
      }

      // Check appLockState match
      if (appLock.appLockState !== appLockState) {
        return false;
      }

      // Check category match - both categoryId and categoryType must match
      // This selector only checks direct links, inheritance is handled in selectAppLockAppliedToCategory
      return appLock.categoryId === category.categoryId && appLock.categoryType === category.categoryType;
    });
  });
};

export const createSelectAppLockLoadedForCategory = (category: Category) => {
  return createSelector(selectAllAppLocks, (appLocks: AppLock[]) => {
    if (!category) {
      return false;
    }

    // Only check the specific category - don't check parents
    // Each category should load its own data, inheritance is handled by display selectors
    return appLocks.some(
      (appLock) => appLock.categoryId === category.categoryId && appLock.categoryType === category.categoryType
    );
  });
};

// ============================================================================
// GENERIC SELECTORS - One-liner selectors for containers
// ============================================================================

/**
 * Generic selector to check if an app lock is linked to a category.
 */
export const selectHasAppLockLinkedToCategory = (
  appLockState: LockState,
  modelName: ModelNameFlags,
  category: Category
) => {
  return createSelector(
    createSelectAppLockLinkedToCategory(appLockState, modelName, category),
    (appLock: AppLock | undefined) => !!appLock
  );
};

/**
 * Selector for checking if app locks are loaded for a specific category.
 * Only checks the specific category - does not check parent categories.
 * This ensures that each category loads its own data, and inheritance is handled
 * by the display selectors (selectAppLockAppliedToCategory) rather than the loaded check.
 *
 * Usage in resolvers:
 * this.store.select(selectAppLocksLoadedForCategory(category))
 */
export const selectAppLockLoadedForCategory = (category: Category): MemoizedSelector<object, boolean> => {
  return createSelectAppLockLoadedForCategory(category);
};

// ============================================================================
// APP LOCKED APPLICATION SELECTORS
// ============================================================================

/**
 * Select App Locked Application linked to category.
 * Returns the application configuration if found, otherwise returns the app lock itself.
 */
export const selectAppLockedApplicationLinkedToCategory = (
  appLockState: LockState,
  modelName: ModelNameFlags,
  category: Category
): Selector<object, ApplicationConfiguration | AppLock> => {
  return createSelector(
    createSelectAppLockLinkedToCategory(appLockState, modelName, category),
    createSelectApplicationsLinkedToCategory(category),
    (appLock, applications) =>
      appLock
        ? applications.find((application) => application.bundleIdentifier === appLock.bundleIdentifier) || appLock
        : undefined
  );
};

/**
 * Select App Locked Application applied to category (with inheritance).
 * Automatically handles inheritance logic for Brand -> Location -> Room.
 */
export const selectAppLockedApplicationAppliedToCategory = (
  appLockState: LockState,
  modelName: ModelNameFlags,
  category: Category
): Selector<object, ApplicationConfiguration | AppLock> => {
  // HACK: These categories do not support inheritance for applications, so we return the applications linked to the category directly.
  // This is a temporary solution until we have a proper plan for our inheritance logic, see user story #35828.
  if (
    category.categoryType === 'Bar' ||
    category.categoryType === 'FrontDesk' ||
    category.categoryType === 'Housekeeping' ||
    category.categoryType === 'Kiosk' ||
    category.categoryType === 'Staff'
  ) {
    return selectAppLockedApplicationLinkedToCategory(appLockState, modelName, category);
  }

  if (category.parent) {
    return createSelector(
      selectAppLockedApplicationAppliedToCategory(appLockState, modelName, category.parent),
      selectAppLockedApplicationLinkedToCategory(appLockState, modelName, category),
      (parent, current) => current ?? parent
    );
  } else {
    return selectAppLockedApplicationLinkedToCategory(appLockState, modelName, category);
  }
};
